import type { ReactNode } from 'react';
import { Box, Flex, Text, UnstyledButton } from '@mantine/core';
import { GradientBorderedInfoCard } from 'src/common/components/card/GradientBorderedInfoCard';
import type { CalendarEvent } from 'src/meetings/models';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { ActionsTab } from 'src/meetings/detail/tabs/ActionsTab';
import { ComplianceTab } from 'src/meetings/detail/tabs/ComplianceTab';
import { SummaryTab } from 'src/meetings/detail/tabs/SummaryTab';
import { TranscriptTab } from 'src/meetings/detail/tabs/TranscriptTab';
import { type Transcript, TranscriptionStatus } from 'src/meetings/detail/tabs/transcript/types';
import type { MeetingScope } from 'src/meetings/scope';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { FamilyMeetingNoteTab } from './FamilyMeetingNoteTab';

export type TabValue =
  | 'summary'
  | 'meeting-note'
  | 'transcript'
  | 'pre-meeting'
  | 'actions'
  | 'compliance';

const BASE_TABS: { value: TabValue; label: string }[] = [
  { value: 'summary', label: 'Summary' },
  { value: 'meeting-note', label: 'Meeting note' },
  { value: 'transcript', label: 'Transcript' },
  { value: 'pre-meeting', label: 'Pre-meeting' },
];

type Props = {
  meeting: CalendarEvent;
  transcript: Transcript | undefined;
  activeTab?: string;
  onTabChange: (tab: TabValue) => void;
  scope: MeetingScope;
  preMeetingContent: ReactNode;
};

export function PastMeetingTabs({
  meeting,
  transcript,
  activeTab,
  onTabChange,
  scope,
  preMeetingContent,
}: Props) {
  const { flagEnabledComplianceChecking } = useFlags();

  const tabs = [
    ...BASE_TABS,
    { value: 'actions' as const, label: 'Actions' },
    ...(flagEnabledComplianceChecking
      ? [{ value: 'compliance' as const, label: 'Compliance' }]
      : []),
  ];

  const tab = tabs.some(t => t.value === activeTab) ? (activeTab as TabValue) : 'summary';

  return (
    <Box>
      <Flex gap="xxxs">
        {tabs.map(({ value, label }) => {
          const isActive = tab === value;
          return (
            <UnstyledButton
              key={value}
              onClick={() => onTabChange(value)}
              bg={isActive ? 'brand.1' : 'white'}
              py="xxs"
              px="xs"
              style={{ borderRadius: 6 }}
            >
              <Text size="textSm" fw={FONT_WEIGHTS.semibold} c={isActive ? 'brand.8' : 'gray.6'}>
                {label}
              </Text>
            </UnstyledButton>
          );
        })}
      </Flex>

      <Box mt="xl">{renderTab(tab, meeting, transcript, scope, preMeetingContent)}</Box>
    </Box>
  );
}

function renderTab(
  tab: TabValue,
  meeting: CalendarEvent,
  transcript: Transcript | undefined,
  scope: MeetingScope,
  preMeetingContent: ReactNode,
) {
  if (tab === 'meeting-note')
    return <FamilyMeetingNoteTab event={meeting} transcript={transcript} scope={scope} />;
  if (tab === 'pre-meeting') return preMeetingContent;
  if (tab === 'actions') return <ActionsTab event={meeting} />;

  if (!transcript || transcript.status !== TranscriptionStatus.READY) {
    if (transcript?.status === TranscriptionStatus.PROCESSING) {
      return (
        <GradientBorderedInfoCard>
          {"Your recording is being transcribed. You'll be able to use this tab once it's ready."}
        </GradientBorderedInfoCard>
      );
    }
    return (
      <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.6">
        No transcript is available for this meeting.
      </Text>
    );
  }

  if (tab === 'compliance') return <ComplianceTab event={meeting} />;

  return tab === 'summary' ? (
    <SummaryTab event={meeting} transcript={transcript} hideSuggestedActions />
  ) : (
    <TranscriptTab event={meeting} transcript={transcript} inlineAudioPlayer scope={scope} />
  );
}
